/**
 * Redaction module for sanitizing sensitive data from captured events.
 * Framework-agnostic - no React dependencies.
 */

// Default sensitive HTTP headers to redact
export const DEFAULT_SENSITIVE_HEADERS = [
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-auth-token',
  'x-session-token',
  'x-csrf-token',
  'x-access-token',
  'x-refresh-token',
  'proxy-authorization',
]

// Default sensitive JSON field names to redact (case-insensitive, substring match)
// These patterns will match any field name containing these substrings
// e.g., 'key' will match 'apiKey', 'secretKey', 'myKey', etc.
export const DEFAULT_SENSITIVE_FIELDS = [
  'password',
  'passwd',
  'secret',
  'token',
  'key', // Matches apiKey, secretKey, etc.
  'auth',
  'bearer',
  'address',
]

const REDACTED_PLACEHOLDER = '[REDACTED]'

/**
 * Normalize a field name by removing case and separators for matching
 * Converts camelCase, snake_case, kebab-case to lowercase without separators
 */
function normalizeFieldName(name) {
  return name
    .replace(/[-_]/g, '') // Remove hyphens and underscores
    .replace(/([a-z])([A-Z])/g, '$1$2') // Handle camelCase
    .toLowerCase()
}

/**
 * Check if a normalized field name contains any sensitive pattern
 */
function containsSensitivePattern(normalizedFieldName, sensitivePatterns) {
  return sensitivePatterns.some((pattern) => normalizedFieldName.includes(pattern.toLowerCase()))
}

/**
 * Redact sensitive headers from a headers object
 */
export function redactHeaders(headers, sensitiveHeaders = DEFAULT_SENSITIVE_HEADERS) {
  if (!headers || typeof headers !== 'object') {
    return headers
  }

  const sensitiveSet = new Set(sensitiveHeaders.map((h) => h.toLowerCase()))
  const redacted = {}

  Object.entries(headers).forEach(([key, value]) => {
    if (sensitiveSet.has(key.toLowerCase())) {
      redacted[key] = REDACTED_PLACEHOLDER
    } else {
      redacted[key] = value
    }
  })

  return redacted
}

/**
 * Recursively redact sensitive fields from a JSON object or array
 */
export function redactJsonBody(body, sensitiveFields = DEFAULT_SENSITIVE_FIELDS) {
  if (!body || typeof body !== 'object') {
    return body
  }

  function redactRecursive(obj) {
    if (Array.isArray(obj)) {
      return obj.map(redactRecursive)
    }

    if (obj && typeof obj === 'object') {
      const redacted = {}
      Object.entries(obj).forEach(([key, value]) => {
        const normalizedKey = normalizeFieldName(key)
        // Check if the field name contains any sensitive pattern
        if (containsSensitivePattern(normalizedKey, sensitiveFields)) {
          redacted[key] = REDACTED_PLACEHOLDER
        } else if (value && typeof value === 'object') {
          redacted[key] = redactRecursive(value)
        } else {
          redacted[key] = value
        }
      })
      return redacted
    }

    return obj
  }

  return redactRecursive(body)
}

/**
 * Truncate a string to a maximum length with a note about truncation
 */
export function truncateBody(body, maxLength = 10000) {
  if (typeof body !== 'string') {
    return body
  }

  if (body.length <= maxLength) {
    return body
  }

  const omitted = body.length - maxLength
  return `${body.substring(0, maxLength)}\n\n[... ${omitted} characters omitted]`
}

/**
 * Redact and truncate a response/request body
 * Handles JSON and text bodies, skips binary content
 */
export function redactBody(body, contentType, options = {}) {
  const { sensitiveFields = DEFAULT_SENSITIVE_FIELDS, maxLength = 10000, captureBody = true } = options

  if (!captureBody) {
    return '[Body capture disabled]'
  }

  if (!body) {
    return body
  }

  // Skip binary content types
  if (contentType && /^(image|audio|video|application\/(octet-stream|pdf|zip))/.test(contentType)) {
    return '[Binary content]'
  }

  // Try to parse and redact JSON
  if (typeof body === 'string' && contentType && /json/.test(contentType)) {
    try {
      const parsed = JSON.parse(body)
      const redacted = redactJsonBody(parsed, sensitiveFields)
      const stringified = JSON.stringify(redacted, null, 2)
      return truncateBody(stringified, maxLength)
    } catch {
      // Not valid JSON, treat as text
    }
  }

  // Handle objects directly (for JSON bodies)
  if (typeof body === 'object') {
    const redacted = redactJsonBody(body, sensitiveFields)
    const stringified = JSON.stringify(redacted, null, 2)
    return truncateBody(stringified, maxLength)
  }

  // Treat as text and truncate
  return truncateBody(String(body), maxLength)
}
